import type { GameState, PuzzleModel } from './types.ts';

/**
 * How each cell of a solve fell: the share's emoji board and the challenge card's
 * picture. Spoiler-free by construction: the board's shape and a state per cell, never
 * letters, clues or answers.
 *
 * - `block`: a clue or blank cell (⬛).
 * - `flow`: a letter cell on an answer solved inside a Flow chain of 2 or more (🟦).
 * - `solo`: a letter cell solved on its own (🟩).
 * - `checked`: a cell a Check flagged wrong at any point (🟨).
 * - `revealed`: a cell of a revealed answer (🟥).
 *
 * Precedence: revealed > checked > flow > solo.
 */
export type CellOutcome = 'block' | 'flow' | 'solo' | 'checked' | 'revealed';

/** The Flow record `solveOutcomes` reads: `FlowState` fits (the core never imports Flow). */
export interface FlowSegments {
	/** Every chain this puzzle, as answer ids in solve order (`FlowState.segments`). */
	segments: readonly (readonly string[])[];
}

/** A chain at least this long is a Flow (the share and the badge agree). */
const FLOW_CHAIN = 2;

/**
 * One outcome per flat cell, in reading order (`row * width + col`). Non-letter cells
 * are `block`. A letter cell is `revealed` if it's in an answer the player revealed,
 * else `checked` if a Check ever flagged it, else `flow` if any answer through it is in
 * a Flow segment of length ≥ 2, else `solo` (which is also how a letter cell of an
 * unfinished solve reads).
 */
export function solveOutcomes(model: PuzzleModel, state: GameState, flow: FlowSegments | null | undefined): CellOutcome[] {
	const revealed = new Set<number>();
	for (const id of state.revealedAnswers ?? []) for (const c of model.answers[id]?.cells ?? []) revealed.add(c);
	const checked = new Set(state.everFlagged ?? []);
	const inFlow = new Set<number>();
	for (const segment of flow?.segments ?? []) {
		if (segment.length < FLOW_CHAIN) continue;
		for (const id of segment) for (const c of model.answers[id]?.cells ?? []) inFlow.add(c);
	}
	return model.kinds.map((kind, cell) => {
		if (kind !== 'letter') return 'block';
		if (revealed.has(cell)) return 'revealed';
		if (checked.has(cell)) return 'checked';
		return inFlow.has(cell) ? 'flow' : 'solo';
	});
}

// ---------------------------------------------------------------------------
// The compact code: the `cells` field on solves and challenges
// ---------------------------------------------------------------------------

/** The format version, the code's first character. */
export const OUTCOMES_VERSION = '1';

const CODES: readonly CellOutcome[] = ['block', 'flow', 'solo', 'checked', 'revealed'];
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * Encodes outcomes as `1` + base64url, 3 bits per cell (block 0, flow 1, solo 2,
 * checked 3, revealed 4), so each character carries two cells in reading order (the
 * first in the high bits) and an odd last cell is padded with a block. 1 + ⌈cells / 2⌉
 * characters: an 8×8 board is 33. The code doesn't carry the shape (the puzzle does):
 * `width` is checked against the length, and a mismatch throws a `RangeError`
 * (a programmer error, like an unknown outcome).
 */
export function encodeOutcomes(outcomes: readonly CellOutcome[], width: number): string {
	if (!Number.isInteger(width) || width <= 0 || outcomes.length % width !== 0) {
		throw new RangeError(`encodeOutcomes: ${outcomes.length} cells don't fill rows of ${width}`);
	}
	const value = (o: CellOutcome | undefined): number => {
		if (o === undefined) return 0;
		const v = CODES.indexOf(o);
		if (v < 0) throw new RangeError(`encodeOutcomes: unknown outcome ${String(o)}`);
		return v;
	};
	let out = OUTCOMES_VERSION;
	for (let i = 0; i < outcomes.length; i += 2) out += B64URL[(value(outcomes[i]) << 3) | value(outcomes[i + 1])];
	return out;
}

/**
 * Decodes `encodeOutcomes` for a `width` × `height` board. Defensive: anything that isn't
 * exactly a version-1 code of that size (wrong prefix or length, a character outside
 * base64url, a value with no outcome, non-block padding, a bad shape) gives null. Never
 * throws.
 */
export function decodeOutcomes(code: unknown, width: number, height: number): CellOutcome[] | null {
	if (typeof code !== 'string' || !Number.isInteger(width) || !Number.isInteger(height)) return null;
	if (width <= 0 || height <= 0) return null;
	const size = width * height;
	if (code[0] !== OUTCOMES_VERSION || code.length !== 1 + Math.ceil(size / 2)) return null;
	const out: CellOutcome[] = [];
	for (let i = 1; i < code.length; i++) {
		const v = B64URL.indexOf(code[i]!);
		if (v < 0) return null;
		const first = CODES[v >> 3];
		const second = CODES[v & 7];
		if (first === undefined || second === undefined) return null;
		out.push(first);
		if (out.length < size) out.push(second);
		else if (second !== 'block') return null;
	}
	return out;
}

// ---------------------------------------------------------------------------
// The emoji board
// ---------------------------------------------------------------------------

/** The share's glyph for each outcome. Every one renders on every platform. */
export const OUTCOME_EMOJI: Readonly<Record<CellOutcome, string>> = {
	block: '⬛',
	flow: '🟦',
	solo: '🟩',
	checked: '🟨',
	revealed: '🟥',
};

/** The board as emoji, one string per row (`width` cells each; a short last row stays short). */
export function emojiBoard(outcomes: readonly CellOutcome[], width: number): string[] {
	if (!Number.isInteger(width) || width <= 0) return [];
	const lines: string[] = [];
	for (let at = 0; at < outcomes.length; at += width) {
		lines.push(outcomes.slice(at, at + width).map((o) => OUTCOME_EMOJI[o] ?? OUTCOME_EMOJI.block).join(''));
	}
	return lines;
}

// ---------------------------------------------------------------------------
// Pace
// ---------------------------------------------------------------------------

/**
 * The pace of a solve, for the challenge's pace ghost: the active elapsed ms at each
 * answer completion, ascending, one per solved answer (reveals included: they count
 * toward "answers done"). Offsets only, no answer ids. Empty for a state saved before
 * the record existed. A copy, safe to send as the solve's `paceMs`.
 */
export function paceOffsets(state: GameState): number[] {
	return [...(state.solveOffsets ?? [])];
}

/**
 * How many answers a pace had done `elapsed` ms in: the ghost's count ("Sam had 9 by
 * now"). Offsets are ascending; ones at exactly `elapsed` count.
 */
export function answersByTime(offsets: readonly number[], elapsed: number): number {
	let n = 0;
	while (n < offsets.length && offsets[n]! <= elapsed) n++;
	return n;
}
